import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Grid, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { parse } from 'query-string'
import * as R from 'ramda'
import {
  type Business,
  CreatableAutocomplete,
  type FieldProp,
  LanguageUtils,
  type Nil,
  PermissionArea,
  PuiTextField,
  Text,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'

import BusinessTypeLabel from '~/components/common/BusinessTypeLabel'
import RequiredFieldsNotice from '~/components/common/inputs/RequiredFieldsNotice'
import Expander from '~/components/common/lists/Expander'
import ClinicLogo from '~/components/common/logos/ClinicLogo'
import PanelAccordion from '~/components/common/PanelAccordion'
import { BusinessTypeName } from '~/constants/businesses'
import FeatureToggle from '~/constants/featureToggle'
import i18n from '~/locales/i18n'
import {
  fetchBusiness,
  fetchBusinessGroupTags,
  updateBusiness,
} from '~/store/actions/businesses'
import {
  getInvalidPanels,
  getSettingsHasChanged,
  updateBusinessFieldsFromSection,
  validateBusinessSections,
} from '~/store/duck/businessSettings'
import { fetchModality } from '~/store/duck/imagingOrders'
import { fetchDevices } from '~/store/duck/labOrders'
import {
  getRhapsodyPayIsFetching,
  getRhapsodyPayIsSaving,
} from '~/store/duck/rhapsodyPay'
import { getCRUDByArea, getCRUDByAreaForBusiness } from '~/store/reducers/auth'
import {
  getBusiness,
  getBusinessIsFetching,
  getBusinessIsSaving,
} from '~/store/reducers/businesses'
import {
  getFeatureToggle,
  getFeatureToggleForBusiness,
  getPracticeTypes,
} from '~/store/reducers/constants'
import type { PracticeMainFields } from '~/types'
import { useBoopEnabled } from '~/utils/boop'
import { getBusinessIsGroup } from '~/utils/businessUtils'
import { usePracticeFieldsSection } from '~/utils/usePracticeFieldsSection'

import {
  useGetPracticeSectionRenderConditions,
  usePracticeDetailsFields,
} from './hooks'
import { PracticeDetailsPanelsComponents } from './PracticeDetailsPanelsComponents'
import { PracticeDetailsTypes } from './PracticeDetailsTypes'
import {
  PracticeDetailsPanels,
  PracticeDetailsPanelsLabels,
  PracticeDetailsPanelsList,
  PracticeDetailsPanelsNoPadding,
} from './practices'
import PracticeStatusSelect from './PracticeStatusSelect'

const useStyles = makeStyles(
  (theme) => ({
    practiceLogo: {
      padding: 0,
      width: 200,
      height: 112,
      boxShadow:
        '1px 1px 2px 0 rgba(60,56,56,0.14), 0 1px 4px 0 rgba(60,56,56,0.12)',
    },
    headerRow: {
      flexWrap: 'nowrap',
      [theme.breakpoints.down('lg')]: {
        flexWrap: 'wrap',
      },
    },
    numberLabel: {
      whiteSpace: 'nowrap',
    },
    autocompleteTag: {
      height: 30,
    },
  }),
  { name: 'PracticeDetails' },
)

const getMainFields = (
  business: Business | Nil,
  isGroup: boolean,
): FieldProp[] => [
  {
    name: 'name',
    label: isGroup
      ? i18n.t('Businesses:GROUP_NAME')
      : i18n.t('Businesses:PRACTICE_NAME'),
    validators: ['required'],
    initialValue: business?.name,
  },
  {
    name: 'internalName',
    label: i18n.t('Businesses:INTERNAL_NAME'),
    initialValue: business?.internalName || '',
  },
  {
    name: 'region',
    label: i18n.t('Businesses:REGION'),
    initialValue: business?.region || '',
  },
  {
    name: 'statusId',
    label: i18n.t('Common:STATUS'),
    type: 'select',
    initialValue: business?.statusId || '',
  },
  {
    name: 'tags',
    label: i18n.t('Common:TAG_ONE_OR_OTHER'),
    type: 'none',
    initialValue: business?.tags || [],
  },
  {
    name: 'practiceTypeIds',
    label: i18n.t('Common:TYPE_ONE_OR_OTHER'),
    type: 'none',
    initialValue: business?.practiceTypeIds || [],
  },
  {
    name: 'logo',
    type: 'none',
    initialValue: business?.logo || '',
  },
]

export interface PracticeDetailsProps {
  itemId: string
  onClose: () => void
}

const PracticeDetails = ({ itemId, onClose }: PracticeDetailsProps) => {
  const classes = useStyles()
  const { search } = useLocation()
  const { panels } = parse(search)
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const dispatch = useDispatch()

  const business = useSelector(getBusiness(itemId))
  const businessGroup = useSelector(getBusiness(business?.parentBusinessId!))
  const isPayConfigFetching = useSelector(getRhapsodyPayIsFetching)
  const isPayConfigSaving = useSelector(getRhapsodyPayIsSaving)
  const isBusinessSaving = useSelector(getBusinessIsSaving)
  const isBusinessFetching = useSelector(getBusinessIsFetching)
  const permissions = useSelector(
    getCRUDByAreaForBusiness(PermissionArea.BUSINESS, business),
  )
  const supportPermissions = useSelector(getCRUDByArea(PermissionArea.SUPPORT))
  const PracticeTypes = useSelector(getPracticeTypes)
  const hasUnsavedData = useSelector(getSettingsHasChanged(itemId))
  const invalidPanels = useSelector(getInvalidPanels(itemId))

  const isAppointmentCancellationReasonEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_CANCELLATION_REASON),
  )
  const isAutoReplyRhapsodyCommunicationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AUTO_REPLY_RHAPSODY_COMMUNICATION),
  )
  const isDiscountReasonEnabled = useSelector(
    getFeatureToggleForBusiness(FeatureToggle.DISCOUNT_REASON, itemId),
  )

  const isBusinessOmniChannel = business?.omniChannel

  const shouldShowAppointmentCancellationToggle =
    isBusinessOmniChannel && isAppointmentCancellationReasonEnabled
  const isFormValid = R.isEmpty(invalidPanels)
  const isSaving = isBusinessSaving || isPayConfigSaving

  const { t } = useTranslation(['Admin', 'Businesses', 'Common'])

  const isGroup = getBusinessIsGroup(business)

  const hideBoopConfig = !useBoopEnabled(business)

  const [expandedPanels, setExpandedPanels] = useState<PracticeDetailsPanels[]>(
    [],
  )

  const { fields, validate, reset } = useFields(
    getMainFields(business, isGroup),
    false,
  )

  const {
    alertConfigurationFields,
    appointmentCommunicationFields,
    appointmentConfigurationFields,
    autoReplyCommunicationsFields,
    boopCommunicationsConfigFields,
    brandingConfigurationFields,
    chewyFields,
    chewyGiftCardFields,
    countrySectionFields,
    discountReasonConfigurationFields,
    generalInformationFields,
    groupDetailsConfigFields,
    invoiceConfigurationsFields,
    kioskConfigurationsFields,
    labelConfigurationFields,
    licensesFields,
    localizationFields,
    mainFields,
    mobileFields,
    npsSystemFields,
    marketplaceManagementFields,
    savedSignatureConfigurationFields,
    sharingFields,
    soapConfigurationFields,
    socialMediaFields,
    recordSharingFields,
    remindersSettingsFields,
    taxConfigurationFields,
    telehealthIntegrationFields,
    vaccineCertificateFooterFields,
    unitsOfMeasurementFields,
    wellnessPlansFields,
  } = usePracticeDetailsFields(itemId)

  usePracticeFieldsSection<PracticeMainFields>({
    business,
    fields,
    sectionName: PracticeDetailsPanels.MAIN,
    validate,
    reset,
  })

  const { name, internalName, region, statusId, tags, practiceTypeIds } = fields

  const sectionRenderConditions = useGetPracticeSectionRenderConditions({
    hasTaxes: Boolean(business?.taxes?.length),
    hideBoopConfig,
    isGroup,
    practiceId: itemId,
  })

  const openPanelWithInvalidField = () => {
    setExpandedPanels([...expandedPanels, ...invalidPanels])
  }

  const save = () => {
    dispatch(validateBusinessSections(itemId))

    if (isFormValid && business?.id) {
      const newBusiness = {
        ...mainFields,
        ...generalInformationFields,
        ...groupDetailsConfigFields,
        ...alertConfigurationFields,

        ...appointmentCommunicationFields,
        ...(!shouldShowAppointmentCancellationToggle
          ? {}
          : appointmentConfigurationFields),
        ...(isAutoReplyRhapsodyCommunicationEnabled
          ? autoReplyCommunicationsFields
          : boopCommunicationsConfigFields),
        ...brandingConfigurationFields,
        ...chewyFields,
        ...(sectionRenderConditions[
          PracticeDetailsPanels.CHEWY_GIFT_CARD_CONFIGURATION
        ]
          ? chewyGiftCardFields
          : {}),
        ...countrySectionFields,
        ...(isDiscountReasonEnabled ? discountReasonConfigurationFields : {}),
        ...invoiceConfigurationsFields,
        ...kioskConfigurationsFields,
        ...labelConfigurationFields,
        ...licensesFields,
        ...localizationFields,
        ...mobileFields,
        ...npsSystemFields,
        ...marketplaceManagementFields,
        ...savedSignatureConfigurationFields,
        ...sharingFields,
        ...soapConfigurationFields,
        ...socialMediaFields,
        ...recordSharingFields,
        ...remindersSettingsFields,
        ...taxConfigurationFields,
        ...telehealthIntegrationFields,
        ...vaccineCertificateFooterFields,
        ...unitsOfMeasurementFields,
        ...wellnessPlansFields,
        id: business?.id,
      }

      dispatch(updateBusiness(newBusiness))
    } else {
      openPanelWithInvalidField()
    }
  }

  const practiceNameInput = (
    <PuiTextField
      disabled={!permissions.update}
      field={name}
      inputProps={{ maxLength: 100 }}
      label={`${name.label}*`}
    />
  )

  useEffect(() => {
    const newPanels = ((panels as PracticeDetailsPanels) || '').split(
      ',',
    ) as PracticeDetailsPanels[]
    if (newPanels.length > 0) {
      setExpandedPanels(newPanels)
    }
  }, [panels])

  useEffect(() => {
    if (itemId) {
      dispatch(fetchBusiness(itemId, false, true))
      dispatch(fetchModality(itemId))
      dispatch(fetchDevices(itemId))
    }
  }, [itemId])

  useEffect(() => {
    if (businessGroup?.id) {
      dispatch(fetchBusinessGroupTags(businessGroup.id))
    }
  }, [businessGroup?.id])

  const handleCheckBoxListChange = (value: string[]) => {
    practiceTypeIds.setValue(value)
    const omniChannelId = Utils.findConstantIdByName(
      BusinessTypeName.OMNICHANNEL,
      PracticeTypes,
    )

    if (value.includes(omniChannelId) && business && business.id) {
      dispatch(
        updateBusinessFieldsFromSection(
          business.id,
          {
            ...invoiceConfigurationsFields,
            estimateSignatureOnApproval: true,
          },
          PracticeDetailsPanels.INVOICE_ESTIMATE_CONFIGURATION,
          true,
        ),
      )
    }
  }

  return (
    <Expander
      expandedItemClass={t('Common:PRACTICE').toLowerCase()}
      hasUnsavedData={isSaving ? false : hasUnsavedData}
      isFetching={isBusinessFetching || isPayConfigFetching}
      isSaving={isSaving}
      showButtons={permissions.update}
      showOverlay={isSaving}
      onBack={onClose}
      onSaveRequested={save}
    >
      <Grid container direction="column" pt={{ xs: 3, md: 0 }}>
        {isGroup ? (
          <Grid container item direction="column">
            {practiceNameInput}
          </Grid>
        ) : (
          <>
            <Grid
              container
              item
              justifyContent={isMobile ? 'center' : 'flex-start'}
            >
              <Grid item>
                {business && (
                  <ClinicLogo
                    allowUpload={permissions.update}
                    className={classes.practiceLogo}
                    clinic={{ ...business, logo: fields.logo.value }}
                    onLogoUploaded={fields.logo.setValue}
                  />
                )}
              </Grid>
              <Grid
                container
                item
                md
                direction="column"
                pl={{ md: 4 }}
                pr={{ md: 7 }}
                pt={{ xs: 2, md: 0 }}
                px={{ xs: 0, sm: 2 }}
              >
                <Grid
                  container
                  alignItems="flex-end"
                  className={classes.headerRow}
                >
                  {practiceNameInput}
                  {supportPermissions.read && (
                    <Text
                      gutterBottom
                      className={classes.numberLabel}
                      ml={{ xs: 0, lg: 2 }}
                    >
                      {t('Admin:PRACTICE.ACCOUNT_NUMBER', { number: itemId })}
                    </Text>
                  )}
                </Grid>
                <Grid
                  container
                  alignItems="flex-end"
                  columnSpacing={2}
                  wrap="nowrap"
                >
                  <Grid item xs={12}>
                    <PuiTextField
                      disabled={!permissions.update}
                      field={internalName}
                      inputProps={{ maxLength: 100 }}
                      label={internalName.label}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PuiTextField
                      disabled={!permissions.update}
                      field={region}
                      inputProps={{ maxLength: 100 }}
                      label={region.label}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              pl={{ md: 0 }}
              pr={{ md: 7 }}
              pt={{ xs: 2, md: 0 }}
              px={{ xs: 0, sm: 2 }}
            >
              <CreatableAutocomplete
                classes={{ tag: classes.autocompleteTag }}
                createLabel={`(${t('Common:NEW_TAG')})`}
                disabled={isBusinessFetching || !permissions.update}
                label={tags.label}
                options={businessGroup?.groupTags || []}
                value={tags.value}
                onValueChange={tags.setValue}
              />
              <Grid container wrap="wrap">
                <Grid container item alignItems="center" wrap="nowrap">
                  {(permissions.update ||
                    Boolean(practiceTypeIds?.value?.length)) && (
                    <Text variant="body">{`${practiceTypeIds.label}:`}</Text>
                  )}
                  {permissions.update ? (
                    <PracticeDetailsTypes
                      practiceTypeIds={practiceTypeIds}
                      onChange={handleCheckBoxListChange}
                    />
                  ) : (
                    <Grid container columnSpacing={2} p={1} wrap="nowrap">
                      {PracticeTypes.filter((item) =>
                        practiceTypeIds.value.includes(item.id),
                      ).map((item) => (
                        <Grid item key={item.id}>
                          <BusinessTypeLabel icon={item.name}>
                            {LanguageUtils.getTranslatedFieldName(item)}
                          </BusinessTypeLabel>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
                <Grid container item alignItems="center" wrap="nowrap">
                  <Text variant="body">{`${t('Common:STATUS')}:`}</Text>
                  <PracticeStatusSelect status={statusId} />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        <Grid
          container
          item
          direction="column"
          mt={3}
          pl={{ md: 0 }}
          pr={{ md: 4 }}
          px={{ xs: 0, sm: 2 }}
        >
          {PracticeDetailsPanelsList.map((panel) => {
            const Component = PracticeDetailsPanelsComponents[panel]
            const shouldRender = Boolean(sectionRenderConditions[panel])
            const noPadding = PracticeDetailsPanelsNoPadding.includes(panel)

            if (!shouldRender || !Component || !business) {
              return null
            }

            const isExpanded = expandedPanels.includes(panel)

            return (
              <PanelAccordion
                expanded={isExpanded}
                key={panel}
                noPadding={noPadding}
                title={PracticeDetailsPanelsLabels[panel]}
                onChange={() =>
                  setExpandedPanels(Utils.toggleListItem(panel, expandedPanels))
                }
              >
                <Component business={business} />
              </PanelAccordion>
            )
          })}
        </Grid>
        <Grid item mt={2} pl={{ xs: 2, md: 0 }}>
          <RequiredFieldsNotice />
        </Grid>
      </Grid>
    </Expander>
  )
}

export default PracticeDetails
